import { NavLink } from "react-router-dom";
import { getBlogSeoPageBySlug } from "../content/blogSeoCluster";
import "../style/seoContentPage.css";

function slugify(text) {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

function LongFormSection({ title, children }) {
  return (
    <section id={slugify(title)}>
      <h2>{title}</h2>
      {children}
    </section>
  );
}

export default function BlogSeoPage({ slug }) {
  const page = getBlogSeoPageBySlug(slug);

  if (!page) {
    return null;
  }

  return (
    <article className="seo-content-page blog-seo-page">
      <header className="seo-content-hero">
        <span>QA Coder Blog</span>
        <h1>{page.title}</h1>
        <p>{page.description}</p>
        <div className="seo-content-actions">
          {page.internalLinks.slice(0, 3).map((link) => (
            <NavLink to={link.to} key={link.to}>
              {link.label}
            </NavLink>
          ))}
        </div>
      </header>

      <div className="seo-content-layout">
        <main className="seo-content-card">
          <LongFormSection title="Overview">
            <p>{page.summary}</p>
            <p>
              This guide is written as a practical blog for {page.audience}. Read it
              slowly, connect every point with a real application, and then use the
              linked QA Coder resources to continue learning. The best way to remember
              this topic is to explain it with one real project example, one interview
              answer, and one testing checklist.
            </p>
            <p>
              In real QA work, a topic becomes useful only when it helps you make a
              better testing decision. That decision can be choosing the right test
              data, deciding what to automate, identifying a defect clearly, or
              explaining release risk to your team. Keep that practical mindset while
              reading this page.
            </p>
          </LongFormSection>

          <LongFormSection title="Key takeaways">
            <ul>
              {page.takeaways.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <p>
              These takeaways are the points you should revise before interviews. Do
              not memorize them as one-line definitions. Convert each takeaway into a
              small story from login, registration, ecommerce, payment, search, API, or
              defect reporting practice.
            </p>
          </LongFormSection>

          <LongFormSection title="Step-by-step learning path">
            <ul>
              {page.steps.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <p>
              Follow these steps in order. First understand the requirement, then write
              scenarios, then create detailed test cases, then execute with proper test
              data, and finally record observations. This sequence trains you to think
              like a real QA engineer rather than a learner who only remembers theory.
            </p>
            <p>
              If you are preparing for automation, use the same steps before writing
              scripts. Automation is stronger when the manual scenario is clear, the
              expected result is measurable, and the test data is predictable.
            </p>
          </LongFormSection>

          <LongFormSection title="Real project examples">
            {page.examples.map((example) => (
              <p key={example}>{example}</p>
            ))}
            <p>
              A strong interview answer should sound practical. For example, instead of
              saying "I test login", say that you verify valid login, wrong password,
              blank fields, locked account, password masking, session handling, logout,
              and role-based navigation. This level of detail shows real QA thinking.
            </p>
            <p>
              You can apply the same pattern to almost every topic. Mention the feature,
              the input data, the expected output, the failure condition, and the
              business impact if that failure reaches production.
            </p>
          </LongFormSection>

          <LongFormSection title="Common mistakes">
            <ul>
              {page.mistakes.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <p>
              Avoiding these mistakes will immediately improve your testing quality.
              Most beginners lose marks in interviews because they know the definition
              but cannot explain the risk, example, or expected result. Keep your
              answers simple, structured, and example-based.
            </p>
          </LongFormSection>

          <LongFormSection title="Detailed QA checklist">
            <p>
              Use this checklist when you revise {page.keyword}. Start by asking what
              business problem the feature solves, who the user is, what data is needed,
              what the happy path looks like, and what can go wrong. Then separate your
              coverage into UI checks, functional checks, validation checks, data checks,
              permission checks, and regression checks.
            </p>
            <p>
              For every important scenario, write one clear expected result. If you
              cannot write the expected result, the requirement is probably unclear.
              That is a signal to ask questions before execution. Good QA work is not
              only clicking screens; it is removing ambiguity before the product reaches
              users.
            </p>
            <p>
              After execution, record what passed, what failed, what was blocked, and
              what still needs regression. This discipline helps you speak confidently
              in daily standups, sprint demos, defect triage, and interviews.
            </p>
          </LongFormSection>

          <LongFormSection title="How to use this in a real project">
            <p>
              In a real project, do not treat this blog as only reading material. Turn
              it into a small practice assignment. Pick one live or demo application,
              create a document with scenarios, write at least ten test cases, execute
              them, and create two sample defect reports. If the topic supports API or
              automation, add one Postman request or one small automation script.
            </p>
            <p>
              This approach gives you project language. Instead of saying "I know the
              topic", you can say "I tested this flow with valid, invalid, boundary,
              permission, and regression scenarios." That is the difference between a
              theoretical answer and a job-ready QA answer.
            </p>
            <p>
              If you are preparing for interviews, record yourself explaining the topic
              in two minutes. Your answer should include a definition, one example, one
              common defect, one testing risk, and one automation or regression idea.
            </p>
          </LongFormSection>

          <LongFormSection title="Interview preparation">
            <ul>
              {page.questions.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <p>
              Practice answering these questions in three parts: definition, example,
              and project usage. This format keeps your answer clear and prevents you
              from sounding memorized. If the interviewer asks a follow-up, explain the
              test data, negative cases, defect reporting, or automation possibility.
            </p>
          </LongFormSection>

          <LongFormSection title="FAQ">
            <div className="seo-faq-list">
              {page.faqs.map((faq) => (
                <details key={faq.question}>
                  <summary>{faq.question}</summary>
                  <p>{faq.answer}</p>
                </details>
              ))}
            </div>
          </LongFormSection>

          <section className="seo-internal-links">
            <h2>Continue learning with QA Coder</h2>
            <p>
              Use these internal guides to connect this blog with manual testing,
              automation testing, API testing, real examples, and interview practice.
            </p>
            <div>
              {page.internalLinks.map((link) => (
                <NavLink to={link.to} key={link.to}>
                  {link.label}
                </NavLink>
              ))}
            </div>
          </section>
        </main>

        <aside className="seo-content-sidebar">
          <div className="seo-sidebar-card">
            <strong>On this blog</strong>
            {["Overview", "Key takeaways", "Step-by-step learning path", "Real project examples", "Common mistakes", "Detailed QA checklist", "How to use this in a real project", "Interview preparation", "FAQ"].map((item) => (
              <a href={`#${slugify(item)}`} key={item}>
                {item}
              </a>
            ))}
          </div>
          <div className="seo-sidebar-card accent">
            <strong>Need a guided roadmap?</strong>
            <p>Join QA Coder for manual testing, automation, API testing, projects, and interview prep.</p>
            <NavLink to="/enroll">Join Live Course</NavLink>
          </div>
        </aside>
      </div>
    </article>
  );
}
